/*
  Licensed under the Apache License, Version 2.0 (the "License"); you may not use
  this file except in compliance with the License. You may obtain a copy of the
  License at

      https://www.apache.org/licenses/LICENSE-2.0

  Unless required by applicable law or agreed to in writing, software distributed
  under the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR
  CONDITIONS OF ANY KIND, either express or implied. See the License for the
  specific language governing permissions and limitations under the License.
*/

import React, { useEffect, useRef, useState } from "react";

import { max, maxBy } from "lodash";

import {
  Line,
  Bar,
  Cell,
  Area,
  Pie,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ReferenceArea,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  PieChart,
  LineChart,
  BarChart,
  AreaChart,
  Label,
} from "recharts";

import Box from "@mui/material/Box";
import { SystemStyleObject } from "@mui/system";
import { Theme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import useTheme from "@mui/styles/useTheme";

import ChartTick from "./tick";
import { useBreakpointConfig } from "../../modules/widgets/utils";
import { SHORT_VIEWPORT_QUERY } from "../../theme";

interface ChartConfig {
  xTitle: string;
  yTitle: string;
  margin: { top: number; right: number; left: number; bottom: number };
  padding: { top: number; right: number; left: number; bottom: number };
  type: string;
  xKey: string;
  yKeys: any;
  xAxis: any;
  yAxis: any;
  cartesianGrid: any;
  gradients: string;
  height: string;
  patterns: string;
  tooltip: any;
  layout: string;
  legend: any;
  unit: string;
  referenceLines?: any[];
  referenceAreas?: any[];
  unitFormat: (v: any) => {};
}

interface ChartProps {
  data: Record<string, any>[];
  config: ChartConfig;
  className?: string;
  handleMouseMove?: () => {};
  handleMouseLeave?: () => {};
}

const findMaxValue = (data, yKeys) => {
  const maxValues = [];

  Object.keys(yKeys).forEach((key) => {
    Object.keys(yKeys?.[key] ?? []).forEach((subKey) => {
      if (data.some((d) => d.hasOwnProperty(subKey))) {
        maxValues.push(maxBy(data, subKey)?.[subKey]);
      }
    });
  });

  return max(maxValues);
};

const MIN_HEIGHT = 100;
const DEFAULT_HEIGHT = 250;
// Per-row height estimate for a side-positioned WidgetLegend (caption + gap),
// used to keep the chart tall enough that legend items aren't clipped.
const SIDE_LEGEND_ITEM_HEIGHT = 22;
const SIDE_LEGEND_PADDING = 16;

export const Chart = (props: ChartProps) => {
  const {} = props;

  const {
    className = "",
    data,
    config,
    handleMouseMove,
    handleMouseLeave,
  } = props;

  const {
    xTitle,
    yTitle,
    margin = { top: 20, right: 10, left: 40, bottom: 0 },
    padding = { top: 0, right: 0, left: 0, bottom: 0 },
    type,
    xKey,
    yKeys,
    xAxis,
    yAxis,
    cartesianGrid,
    gradients,
    height,
    patterns,
    tooltip,
    layout,
    legend,
    unit,
    referenceLines,
    referenceAreas,
    unitFormat,
  } = config;

  const { lines, bars, areas, pies } = yKeys;

  const maxYValue = findMaxValue(data, config);
  const isShortViewport = useMediaQuery(SHORT_VIEWPORT_QUERY);
  const baseHeight = Math.max(
    Number(height?.replace?.(/px$/, "") || height) || DEFAULT_HEIGHT,
    MIN_HEIGHT
  );
  const hasSideLegend =
    !!legend && ["left", "right"].includes(legend?.position);
  const sideLegendMinHeight = hasSideLegend
    ? (Array.isArray(data) ? data.length : 0) * SIDE_LEGEND_ITEM_HEIGHT +
      SIDE_LEGEND_PADDING
    : 0;
  const shrunkHeight = Math.max(Math.round(baseHeight * 0.55), MIN_HEIGHT);
  const effectiveHeight = Math.max(
    isShortViewport ? shrunkHeight : baseHeight,
    sideLegendMinHeight
  );

  let CHART;

  switch (type?.replace(/_widget$/, "")) {
    case "pie":
      CHART = PieChart;
      break;
    case "bar":
      CHART = BarChart;
      break;
    case "line":
      CHART = LineChart;
      break;
    case "area":
      CHART = AreaChart;
      break;
    default: {
      CHART = ComposedChart;
    }
  }

  const theme = useTheme();

  // Recharts 1.x drops the legend's first size report if the chart re-renders in
  // the same commit (its legend ref is briefly null), leaving the legend over the
  // x axis until something else re-renders the chart. Re-render once the chart
  // is laid out, and whenever the legend resizes so it re-measures.
  const containerRef = useRef<HTMLDivElement>(null);
  const [legendMeasureId, setLegendMeasureId] = useState(0);
  const hasLegend = !!legend;

  useEffect(() => {
    const container = containerRef.current;
    if (!hasLegend || !container || typeof ResizeObserver === "undefined") {
      return;
    }

    let observedLegend: Element | null = null;
    const observer = new ResizeObserver(() => {
      const legendWrapper = container.querySelector(".recharts-legend-wrapper");
      if (legendWrapper && legendWrapper !== observedLegend) {
        observedLegend = legendWrapper;
        observer.observe(legendWrapper);
      }
      setLegendMeasureId((id) => id + 1);
    });
    observer.observe(container);

    return () => observer.disconnect();
  }, [hasLegend]);

  const responsiveXAxisConfig = useBreakpointConfig(xAxis?.responsive);
  const responsiveXAxis = xAxis
    ? {
        ...xAxis,
        ...responsiveXAxisConfig,
      }
    : null;

  if (responsiveXAxis) {
    delete responsiveXAxis.responsive;
  }

  const titleProps: SystemStyleObject<Theme> = {
    position: "relative",
    marginTop: 1,
    paddingTop: 0.5,
    // TypographyStyle's "@font-face" slot is not valid sx
    ...(theme.typography.caption as SystemStyleObject<Theme>),
  };

  const styles: Record<"xTitle" | "yTitle", SystemStyleObject<Theme>> = {
    xTitle: {
      ...titleProps,
      textAlign: "right",
    },
    yTitle: {
      ...titleProps,
    },
  };

  const effectiveLayout = type === "pie" ? "centric" : layout || "horizontal";

  const legendProps = {
    data: data,
    // Unused by Legend; changing it defeats PureComponent so it re-measures
    legendMeasureId,
    ...legend,
    ...(hasSideLegend ? { height: effectiveHeight } : {}),
    ...(isShortViewport
      ? {
          wrapperStyle: {
            ...legend?.wrapperStyle,
            fontSize: "0.7rem",
          },
        }
      : {}),
  };

  return (
    <Box ref={containerRef} className={`marapp-qa-chart ${className}`} my={2}>
      {yTitle && <Box sx={styles.yTitle}>{yTitle}</Box>}
      <ResponsiveContainer width="100%" height={effectiveHeight}>
        <CHART
          data={data}
          height={effectiveHeight}
          layout={effectiveLayout}
          margin={margin}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          padding={padding}
        >
          <defs>
            {gradients &&
              Object.keys(gradients).map((key) => (
                <linearGradient
                  key={`lg_${key}`}
                  {...gradients[key].attributes}
                >
                  {gradients[key].stops &&
                    Object.keys(gradients[key].stops).map((sKey) => (
                      <stop
                        key={`st_${sKey}`}
                        {...gradients[key].stops[sKey]}
                      />
                    ))}
                </linearGradient>
              ))}

            {patterns &&
              Object.keys(patterns).map((key) => (
                <pattern key={`pattern_${key}`} {...patterns[key].attributes}>
                  {patterns[key].children &&
                    Object.keys(patterns[key].children).map((iKey) => {
                      const { tag } = patterns[key].children[iKey];

                      return React.createElement(tag, {
                        key: iKey,
                        ...patterns[key].children[iKey],
                      });
                    })}
                </pattern>
              ))}
          </defs>

          {xAxis && (
            <XAxis
              dataKey={xKey || ""}
              axisLine={false}
              tickLine={false}
              tick={{
                dy: 8,
                fontSize: "11px",
                fontWeight: 500,
                fill: "#FFF",
              }}
              {...responsiveXAxis}
            />
          )}

          {yAxis && (
            <YAxis
              axisLine={false}
              tickSize={-5}
              mirror
              tickMargin={0}
              tickLine={false}
              tick={
                <ChartTick
                  dataMax={maxYValue}
                  unit={unit || ""}
                  unitFormat={unitFormat || ((value) => value)}
                  fill="#FFF"
                  fontWeight={500}
                />
              }
              {...yAxis}
            />
          )}

          {cartesianGrid && (
            <CartesianGrid
              strokeWidth={0.5}
              stroke="#999"
              shapeRendering="crispEdges"
              {...cartesianGrid}
            />
          )}
          {referenceAreas &&
            Object.keys(referenceAreas).map((key) => {
              return (
                <ReferenceArea
                  key={key}
                  dataKey={key}
                  {...referenceAreas[key]}
                />
              );
            })}

          {areas &&
            Object.keys(areas).map((key) => {
              const { label, ...areaProps } = areas[key];
              return (
                <Area
                  key={key}
                  dataKey={key}
                  dot={false}
                  {...areaProps}
                  {...(typeof label !== "string" && label != null && { label })}
                />
              );
            })}

          {bars &&
            Object.keys(bars).map((key) => (
              <Bar key={key} dataKey={key} dot={false} {...bars[key]}>
                {!!bars[key].label && <Label {...bars[key].label} />}

                {bars[key].itemColor &&
                  data.map((item: any) => (
                    <Cell
                      key={`c_${item.color}_${item.name}`}
                      fill={item.color}
                      stroke={item.strokeColor ?? undefined}
                      strokeWidth={item.strokeColor ? 1 : undefined}
                    />
                  ))}
              </Bar>
            ))}

          {lines &&
            Object.keys(lines).map((key) => (
              <Line
                key={key}
                dataKey={key}
                dot={false}
                strokeWidth={2}
                {...lines[key]}
              />
            ))}

          {pies &&
            Object.keys(pies).map((key) => {
              const pieData = data.filter(
                (item: any) => !!item.percentage && item[key]
              );
              return (
                <Pie
                  key={key}
                  data={pieData}
                  dataKey={key}
                  startAngle={450}
                  paddingAngle={2}
                  endAngle={90}
                  {...pies[key]}
                >
                  {pieData.map((item) => (
                    <Cell
                      key={`c_${item[pies[key].colorKey || "color"]}`}
                      fill={item[pies[key].colorKey || "color"]}
                      stroke={
                        item.strokeColor ?? item[pies[key].colorKey || "color"]
                      }
                      strokeWidth={item.strokeColor ? 1 : undefined}
                    />
                  ))}
                </Pie>
              );
            })}
          {referenceLines &&
            referenceLines.map((referenceLine) => {
              return (
                <ReferenceLine
                  {...referenceLine}
                  key={`refline-${referenceLine.x}`}
                />
              );
            })}

          {/* we need to draw this after the graph, as some elements in a vertical bar will draw above the graph */}
          {layout === "vertical" && responsiveXAxis && (
            <XAxis
              tick={{ fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              {...responsiveXAxis}
            />
          )}

          {tooltip && <Tooltip isAnimationActive={false} {...tooltip} />}
          {legend && <Legend {...legendProps} />}
        </CHART>
      </ResponsiveContainer>

      {xTitle && <Box sx={styles.xTitle}>{xTitle}</Box>}
    </Box>
  );
};
